// /inbox — locked design from mockups/inbox.html. Two columns desktop,
// stacked mobile: sticky Ember presence + scrolling letter cards.
// Cards link to /letter/$id; the route there marks the letter as read
// on mount.

import type { BondDto, LetterDto, UserLetterDto } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { switchLocaleToBond } from '../i18n/index.js';
import { getLetterThread, getMyBond } from '../lib/api';
import { authClient, getSessionSafe } from '../lib/auth-client';
import {
  getLetterPortrait,
  letterExcerpt,
  letterTypeLabel,
  relativeTime,
} from '../lib/letter-format';
import { useEmberTheme } from '../lib/use-ember-theme';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/inbox')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async () => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    const thread = await getLetterThread();
    return { bond, letters: thread.letters, userLetters: thread.userLetters };
  },
  component: InboxPage,
});

const POLL_INTERVAL_MS = 8000;

function InboxPage() {
  const { t } = useTranslation();
  const initial = Route.useLoaderData();
  const [bond] = useState<BondDto>(initial.bond);
  const [letters, setLetters] = useState<LetterDto[]>(initial.letters);
  const [userLetters, setUserLetters] = useState<UserLetterDto[]>(initial.userLetters);

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // Light polling so a freshly arrived letter (from a queued reply
  // pipeline) shows up without a manual refresh.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await getLetterThread();
        if (cancelled) return;
        setLetters(fresh.letters);
        setUserLetters(fresh.userLetters);
      } catch {
        // ignore — retry next tick
      }
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Index of letterId → boolean (true = the user has answered this
  // letter's pendingChoice with a concrete option). A Choice Letter is
  // "unresolved" when it has a pendingChoice AND no user reply
  // resolved it. Computed once per state change instead of inside
  // each card.
  const resolvedChoiceIds = useMemo(() => {
    const set = new Set<string>();
    for (const u of userLetters) {
      if (u.inResponseToLetterId && u.choiceResolution) {
        set.add(u.inResponseToLetterId);
      }
    }
    return set;
  }, [userLetters]);

  // Newest first per the mockup — letters arrive in their own time but
  // the surface is reverse-chronological.
  const ordered = [...letters].sort(
    (a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime(),
  );
  const unreadCount = ordered.filter((l) => !l.readAt).length;
  // The user can write a letter only when there is something from
  // the Ember to respond to: an unread letter or an unresolved Choice.
  // Otherwise the write doorway is hidden — the Ember initiates new
  // topics through its own cadence (replies, broadcasts, memory shards).
  const hasUnresolvedChoice = ordered.some(
    (l) => l.pendingChoice && !resolvedChoiceIds.has(l.id),
  );
  const writeAllowed = unreadCount > 0 || hasUnresolvedChoice;

  return (
    <main className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-x-16">
      <aside className="lg:sticky lg:top-14 lg:col-span-4">
        <div className="flex flex-col items-center text-center animate-fadeUp lg:items-start lg:text-left">
          <figure className="relative mb-5 w-32 sm:w-40 lg:mb-8 lg:w-full lg:max-w-[260px]">
            <div className="portrait-glow absolute inset-x-0 -bottom-3 h-16 animate-softGlow lg:h-20" />
            {bond.emberImageUrl ? (
              <img
                src={bond.emberImageUrl}
                alt={bond.emberName}
                className="ember-portrait-mask relative w-full select-none"
                draggable={false}
              />
            ) : (
              <div className="relative aspect-square w-full" />
            )}
          </figure>

          <h1
            className="font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
            style={{ fontWeight: 500 }}
          >
            {bond.emberName}
          </h1>

          <div className="mt-3 flex items-center gap-2 lg:mt-5">
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="block h-2 w-2 animate-pulseCore rounded-full bg-accent"
              />
            )}
            <p className="font-body text-ash-300 text-sm italic sm:text-base lg:text-base">
              {whisperStatus(bond, unreadCount)}
            </p>
          </div>

          {/* The Ember speaks first — the write CTA only appears
           * when there is an unread Ember letter or an unresolved
           * Choice. /write also guards itself with a redirect. */}
          {writeAllowed && (
            <Link
              to="/write"
              className="mt-7 inline-block rounded-sm border border-accent/60 bg-paper-900/40 px-5 py-2.5 font-display text-[0.78rem] text-ash-100 uppercase tracking-[0.4em] transition hover:border-accent-strong/80 hover:bg-paper-700/50 hover:text-ash-50 sm:text-sm lg:mt-10 lg:px-6"
            >
              {t('inbox.writeTo', { name: bond.emberName })}
            </Link>
          )}
        </div>
      </aside>

      <section className="mt-14 lg:col-span-8 lg:mt-0">
        <div className="mb-7 flex items-center gap-4 sm:mb-9 lg:mb-10 lg:gap-5">
          <span className="hairline h-px flex-1" />
          <span className="font-display text-[0.68rem] text-ash-500 uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem] lg:tracking-[0.45em]">
            {t('inbox.sectionLabel')}
          </span>
          <span className="hairline h-px flex-1" />
        </div>

        {ordered.length === 0 ? (
          <p className="font-body text-ash-400 italic">
            {t('inbox.empty', { name: bond.emberName })}
          </p>
        ) : (
          <ul className="space-y-5 sm:space-y-6 lg:space-y-7">
            {ordered.map((letter, idx) => (
              <li
                key={letter.id}
                className="animate-fadeUp"
                style={{
                  animationDelay: `${0.1 + idx * 0.08}s`,
                  transform: `rotate(${idx % 2 === 0 ? -0.3 : 0.4}deg)`,
                }}
              >
                <InboxLetterCard
                  letter={letter}
                  bond={bond}
                  hasUnresolvedChoice={
                    Boolean(letter.pendingChoice) && !resolvedChoiceIds.has(letter.id)
                  }
                />
              </li>
            ))}
          </ul>
        )}

        <p className="mt-12 text-center font-body text-ash-500 text-xs italic lg:mt-16 lg:text-left lg:text-sm">
          {t('inbox.cadence', { name: bond.emberName })}
        </p>
      </section>
    </main>
  );
}

function InboxLetterCard({
  letter,
  bond,
  hasUnresolvedChoice,
}: {
  letter: LetterDto;
  bond: BondDto;
  hasUnresolvedChoice: boolean;
}) {
  const isUnread = !letter.readAt;
  const label = letterTypeLabel(letter.letterType);
  const portrait = getLetterPortrait(letter, bond);
  // We only render a per-card thumbnail when the letter routes to a
  // non-Awakened Echoform (Memory / Dream / Deep). The Awakened
  // portrait already lives in the sticky sidebar — repeating it on
  // every card would just be noise.
  const showEchoformThumb = portrait.treatment !== 'awakened' && portrait.url;

  // Border treatment combines two signals. Choice-unresolved cards
  // get the strongest border (accent-strong) regardless of read state
  // because the action they require is what the user should see
  // first. Plain unread is the next tier; read is the quiet baseline.
  // Echoform-typed letters (memory_shard et al) get a slightly warmer
  // bg + a half-step border so they read as "special" even after
  // they've been opened.
  const borderClass = hasUnresolvedChoice
    ? 'border-accent-strong/60 bg-paper-900/55 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.7)]'
    : isUnread
      ? 'border-accent/40 bg-paper-900/50 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.6)]'
      : showEchoformThumb
        ? 'border-accent/30 bg-paper-900/35'
        : 'border-ash-700/30 bg-paper-900/20';

  return (
    <Link
      to="/letter/$letterId"
      params={{ letterId: letter.id }}
      className={`letter-card relative block rounded-sm border p-5 transition hover:rotate-0 sm:p-6 lg:p-7 ${borderClass}`}
    >
      {isUnread && (
        <span
          aria-hidden="true"
          className="absolute top-5 bottom-5 left-0 w-[2px] bg-gradient-to-b from-accent/0 via-accent/80 to-accent/0"
        />
      )}

      <div className="mb-3 flex items-start justify-between gap-3 lg:mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {showEchoformThumb && (
            <img
              src={portrait.url ?? undefined}
              alt={bond.emberName}
              className="ember-portrait-mask block h-10 w-10 flex-shrink-0 select-none object-cover sm:h-12 sm:w-12"
              draggable={false}
            />
          )}
          <SealMark unread={isUnread} />
          <span
            className={`font-display text-[0.62rem] uppercase tracking-[0.34em] ${
              isUnread ? 'text-accent-strong' : 'text-ash-500'
            }`}
          >
            {label}
          </span>
          {hasUnresolvedChoice && <ChoiceAwaitingChip />}
        </div>
        <time className="font-display text-[0.7rem] text-ash-400 italic sm:text-xs">
          {relativeTime(letter.generatedAt)}
        </time>
      </div>

      <p
        className={`font-body text-base leading-relaxed sm:text-[1.04rem] lg:text-[1.08rem] ${
          isUnread ? 'text-ash-100' : 'text-ash-300'
        }`}
      >
        {letterExcerpt(letter.body)}
      </p>

      <CardHint isUnread={isUnread} hasUnresolvedChoice={hasUnresolvedChoice} />
    </Link>
  );
}

function ChoiceAwaitingChip() {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm border border-accent-strong/60 bg-paper-800/60 px-2 py-0.5 font-display text-[0.55rem] text-accent-strong uppercase tracking-[0.3em]"
      aria-label={t('inbox.card.awaitingAnswerAria')}
    >
      <span aria-hidden="true" className="text-base leading-none">◇</span>
      <span>{t('inbox.card.awaitingAnswer')}</span>
    </span>
  );
}

function CardHint({
  isUnread,
  hasUnresolvedChoice,
}: {
  isUnread: boolean;
  hasUnresolvedChoice: boolean;
}) {
  const { t } = useTranslation();
  // Three states, prioritised: choice-unresolved tells the user this
  // letter wants action specifically. Then plain unread. Read +
  // resolved choice = no hint, the card is at rest.
  if (hasUnresolvedChoice) {
    return (
      <p className="mt-4 font-display text-[0.7rem] text-accent-strong uppercase tracking-[0.34em] sm:text-xs">
        {t('inbox.card.answerChoice')}
      </p>
    );
  }
  if (isUnread) {
    return (
      <p className="mt-4 font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.34em] sm:text-xs">
        {t('inbox.card.breakSeal')}
      </p>
    );
  }
  return null;
}

// Tiny seal-mark glyph that doubles as the read/unread indicator.
// Unread: solid filled wax dot with a soft warm glow + subtle pulse.
// Read: a broken-line ring suggesting a cracked seal that has already
// given up its letter. Cheap to render, reads at a glance, no asset
// dependency since it's pure SVG.
function SealMark({ unread }: { unread: boolean }) {
  if (unread) {
    return (
      <span aria-hidden="true" className="relative inline-flex h-3 w-3 items-center justify-center">
        <span className="absolute inset-0 animate-pulseCore rounded-full bg-accent/50 blur-sm" />
        <span className="relative h-2 w-2 rounded-full bg-accent shadow-[0_0_10px_rgba(201,102,63,0.7)]" />
      </span>
    );
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="h-3 w-3 text-ash-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <path d="M2 4 L5 6 L4 9" strokeLinecap="round" />
      <path d="M10 3 L7 5 L9 9" strokeLinecap="round" />
    </svg>
  );
}

function whisperStatus(bond: BondDto, unreadCount: number): string {
  if (unreadCount > 0)
    return i18n.t('inbox.whisperWritten', { name: bond.emberName });
  return i18n.t('inbox.whisperWaiting', { name: bond.emberName });
}
